import axios from 'axios'
import Request, { apiBaseUrl } from './index'
import type { RequestConfig } from './types'
import type { ApiEnvelope } from '@etherworks/shared-types'

// ─── Shared-auth adapters ──────────────────────────────────────────────────
// Bypass the legacy `{code, data, msg}` interceptor by calling /api/auth/*
// directly with raw axios, then mapping responses to the legacy shape.

const authBase = `${apiBaseUrl}/auth`;

const rawAuth = axios.create({
	baseURL: authBase,
	withCredentials: true,
	timeout: 30000,
	validateStatus: () => true,
});

const svgToDataUrl = (svg: string): string => {
	const b64 = typeof window !== 'undefined' && (window as any).btoa
		? window.btoa(unescape(encodeURIComponent(svg)))
		: Buffer.from(svg, 'utf-8').toString('base64');
	return `data:image/svg+xml;base64,${b64}`;
};

const errMsg = (res: any, fallback: string): string =>
	res?.data?.error || res?.data?.message || fallback;

const userLanguage = (user: any): string | undefined => {
	const language = user?.preferences?.language;
	return typeof language === 'string' && language ? language : undefined;
};

// GET /auth/captcha → { code:0, data:{ captchaId, imageBase64 } }
export const generateCaptcha = async () => {
	const res = await rawAuth.get('/captcha');
	if (res.status === 200 && res.data?.id && res.data?.svg) {
		return {
			code: 0,
			data: { captchaId: res.data.id, imageBase64: svgToDataUrl(res.data.svg) },
		};
	}
	return { code: -1, msg: errMsg(res, 'captcha failed') };
};

// POST /auth/login
export const login = async (data: any) => {
	const body = data.challenge_token
		? {
			challengeToken: data.challenge_token,
			...(data.totp_code ? { twoFactorCode: data.totp_code } : {}),
		}
		: {
			email: data.email,
			password: data.password,
			captchaId: data.captcha_id,
			captchaAnswer: data.captcha_code,
			rememberMe: !!data.auto_login,
			...(data.totp_code ? { twoFactorCode: data.totp_code } : {}),
		};
	const res = await rawAuth.post('/login', body);
	if (res.status === 200 && res.data?.user) {
		return { code: 0, email: res.data.user.email, role: res.data.user.role || 'user', language: userLanguage(res.data.user) };
	}
	if (res.status === 200 && res.data?.requires2FA) {
		return { code: -16, challengeToken: res.data.challengeToken };
	}
	if (res.status === 401) {
		const err = String(res.data?.error || '');
		if (err.toLowerCase().includes('2fa')) return { code: -17, msg: err };
		return { code: -6, msg: err || 'Invalid credentials' };
	}
	if (res.status === 403 && res.data?.needsEmailVerification) {
		return { code: -5, msg: errMsg(res, 'Email not verified') };
	}
	if (res.status === 400) {
		const err = String(res.data?.error || '');
		if (err.toUpperCase().includes('CAPTCHA')) return { code: -20, msg: err };
		return { code: -1, msg: err };
	}
	return { code: -1, msg: errMsg(res, 'login failed') };
};

// POST /auth/logout
export const signout = async (): Promise<any> => {
	await rawAuth.post('/logout');
	return { code: 0 };
};

// "Send register code" → actually creates the account in shared-auth (which
// also sends the verification email). Caller must already have password+confirm
// filled in. UI calls this from the captcha modal.
export const sendRegisterCode = async (data: {
	email: string;
	captcha_id: string;
	captcha_code: string;
}) => {
	const body = {
		email: data.email,
		captchaId: data.captcha_id,
		captchaAnswer: data.captcha_code,
	};
	const res = await rawAuth.post('/register', body);
	if (res.status === 200 && res.data?.user) return { code: 0 };
	if (res.status === 409) {
		// Account exists. If unverified, resend code; if verified, surface error.
		const resend = await rawAuth.post('/resend-verification', { email: data.email });
		if (resend.status === 200) return { code: 0 };
		return { code: -9, msg: errMsg(resend, 'email already registered') };
	}
	if (res.status === 400) {
		const err = String(res.data?.error || '');
		if (err.toUpperCase().includes('CAPTCHA')) return { code: -20, msg: err };
		return { code: -1, msg: err };
	}
	return { code: -50, msg: errMsg(res, 'send code failed') };
};

// "Register account" → in the shared-auth flow this just verifies the email
// code (the account was created in sendRegisterCode). Password change is not
// supported here — register.tsx must use the same password it sent earlier.
export const registerAccount = async (data: {
	email: string;
	email_code: string;
	password: string;
	confirm_password: string;
	referral_code?: string;
}) => {
	const res = await rawAuth.post('/verify-email', { email: data.email, code: data.email_code, password: data.password });
	if (res.status === 200) return { code: 0, role: 'user' };
	if (res.status === 400) return { code: -11, msg: errMsg(res, 'invalid code') };
	return { code: -1, msg: errMsg(res, 'verify failed') };
};

// "Reset password" → request the reset code email
export const resetPassword = async (data: {
	email: string;
	captcha_id: string;
	captcha_code: string;
}) => {
	const res = await rawAuth.post('/request-password-reset', {
		email: data.email,
		captchaId: data.captcha_id,
		captchaAnswer: data.captcha_code,
	});
	if (res.status === 200) return { code: 0, message: String(res.data?.message || '') };
	if (res.status === 400) {
		const err = String(res.data?.error || '');
		if (err.toUpperCase().includes('CAPTCHA')) return { code: -20, msg: err };
		return { code: -1, msg: err };
	}
	return { code: -1, msg: errMsg(res, 'request reset failed') };
};

// "Reset password by email" → submit code + new password
export const resetPasswordByEmail = async (data: {
	email: string;
	email_code: string;
	password: string;
	confirm_password: string;
}) => {
	const res = await rawAuth.post('/reset-password', {
		email: data.email,
		code: data.email_code,
		newPassword: data.password,
	});
	if (res.status === 200) return { code: 0, message: String(res.data?.message || '') };
	if (res.status === 400) return { code: -11, msg: errMsg(res, 'reset failed') };
	return { code: -1, msg: errMsg(res, 'reset failed') };
};

interface cloudRequestConfig<T> extends RequestConfig {
	data?: T
}
type CloudResponse<T> = Omit<ApiEnvelope<T>, 'data'> & (undefined extends T ? { data?: T } : { data: T }) & { message?: string }

const cloudRequest = <D, T = any>(config: cloudRequestConfig<D>) => {
	const { method = 'GET' } = config;
	if(method == 'get' || method == 'GET') {
		config.params = config?.data;
	}
	// Pass through timeout if specified
	return Request.request<CloudResponse<T>>(config);
}

const commonInterceptors = {
	requestInterceptors(res: any) {
		return res;
	},
	responseInterceptors(result: any) {
		return result;
	}
};

// Get current user info (including role) → adapter over /api/auth/me
export const getUserInfo = async (): Promise<{ code: number; email: string; role: string; language?: string; msg?: string }> => {
	const res = await rawAuth.get('/me');
	if (res.status === 200 && res.data?.user) {
		return { code: 0, email: res.data.user.email, role: res.data.user.role || 'user', language: userLanguage(res.data.user) };
	}
	return { code: -7, email: '', role: '', msg: errMsg(res, 'unauthorized') };
}

export const updateLanguagePreference = async (language: string): Promise<{ code: number; msg?: string }> => {
	const res = await rawAuth.patch('/preferences', { language });
	if (res.status === 200) return { code: 0 };
	return { code: -1, msg: errMsg(res, 'update language failed') };
}

// =====================================================
// PayPal Payment APIs
// =====================================================

// Get PayPal client ID for SDK initialization
export const getPayPalClientId = () => {
	interface Res {
		client_id: string
		mode: 'sandbox' | 'live'
	}
	return cloudRequest<{}, Res>({
		url: '/payments/paypal/client-id',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Create PayPal order (creates local order + PayPal order in one step)
export const createPayPalOrder = (amount: number) => {
	interface Res {
		paypal_order_id: string
		order_id: string
		amount: number
	}
	return cloudRequest<{ amount: number }, Res>({
		url: '/payments/paypal/create-order',
		method: 'POST',
		data: { amount },
		interceptors: commonInterceptors
	})
}

// Capture PayPal order after user approval
export const capturePayPalOrder = (orderId: string, paypalOrderId: string) => {
	interface Res {
		success: boolean
		order_id: string
		paypal_order_id: string
		amount: number
		new_balance: number
	}
	return cloudRequest<{ order_id: string; paypal_order_id: string }, Res>({
		url: '/payments/paypal/capture-order',
		method: 'POST',
		data: { order_id: orderId, paypal_order_id: paypalOrderId },
		interceptors: commonInterceptors
	})
}

// Process credit card payment
export const processCreditCardPayment = (data) => {
	interface Req {
		order_id: number
		card_number?: string
		expiry?: string
		cvc?: string
		cardholder_name?: string
		payment_method_id?: string
		save_card?: boolean
		billing_details?: any
	}
	return cloudRequest<Req, void>({
		url: '/payments/creditcard',
		method: 'POST',
		data,
		interceptors: commonInterceptors
	})
}

// Process credit card payment with backend delay (simulates decline)
// Creates order with payment_method: 'credit_card' and returns decline after 5-8s
export const processCreditCardPaymentWithDelay = (data: {
	payment_method_id?: string
	amount: number
}) => {
	return cloudRequest<typeof data, void>({
		url: '/payments/creditcard/process',
		method: 'POST',
		data,
		interceptors: commonInterceptors
	})
}

// Get saved payment methods
export const getPaymentMethods = () => {
	interface Res {
		id: string
		type: string
		is_default: boolean
		card: {
			holder_name: string
			last4: string
			brand: string
			expiry_month: string
			expiry_year: string
		}
		billing_details: any
		created_at: number
	}
	return cloudRequest<{}, Res[]>({
		url: '/payments/methods',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Add new payment method
export const addPaymentMethod = (data: {
	card: {
		number: string
		holder_name: string
		expiry_month: string
		expiry_year: string
		cvc: string
	}
	billing_details: {
		address_line1: string
		address_line2?: string
		city?: string
		state?: string
		postal_code?: string
		country: string
	}
}) => {
	return cloudRequest<typeof data, void>({
		url: '/payments/methods',
		method: 'POST',
		data,
		interceptors: commonInterceptors
	})
}

// Delete payment method
export const deletePaymentMethod = (id: string) => {
	return cloudRequest<{}, void>({
		url: `/payments/methods/${id}`,
		method: 'DELETE',
		interceptors: commonInterceptors
	})
}

// Set default payment method
export const setDefaultPaymentMethod = (id: string) => {
	return cloudRequest<{}, void>({
		url: `/payments/methods/${id}/default`,
		method: 'PUT',
		interceptors: commonInterceptors
	})
}
// User center dashboard
export const userDashboard = () => {
	return cloudRequest({
		url: '/user/dashboard',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Gift Card APIs
export interface GiftCard {
	id: string
	card_number_masked: string
	card_number: string
	password: string | null  // Password for unredeemed cards, null if redeemed
	amount: number
	created_at: number
	is_redeemed: boolean
	redeemed_by: string | null
	redeemed_at: number | null
}

// Get user's generated gift cards
export const getMyGiftCards = () => {
	return cloudRequest<{}, GiftCard[]>({
		url: '/user/gift-cards',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Compute API Functions
export const runpodRequest = (method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH', endpoint: string, data?: any, timeout?: number) => {
	return cloudRequest<any, any>({
		url: `/compute${endpoint}`,
		method,
		data,
		timeout: timeout || 30000, // Default 30s timeout for RunPod operations
		interceptors: commonInterceptors
	})
}

// Get all pods
export const getRunpodPods = () => {
	return runpodRequest('GET', '/pods')
}

// Get single pod by ID (more efficient for polling individual pod status)
export const getRunpodPod = (podId: string) => {
	return runpodRequest('GET', `/pods/${podId}`)
}

// Create on-demand pod
export const createRunpodPod = (data: any) => {
	return runpodRequest('POST', '/pods', data)
}

// Create spot pod (interruptable, with bidding)
export const createRunpodSpotPod = (data: any) => {
	return runpodRequest('POST', '/pods', {
		...data,
		isSpot: true
	})
}

// Start on-demand pod (resume)
export const startRunpodPod = (podId: string, gpuCount?: number) => {
	return runpodRequest('PATCH', `/pods/${podId}`, {
		action: 'resume',
		gpuCount: gpuCount || 1
	})
}

// Start spot pod (bid resume)
export const startRunpodSpotPod = (podId: string, gpuCount: number = 1, bidPerGpu: number = 0.2) => {
	return runpodRequest('PATCH', `/pods/${podId}`, {
		action: 'resume',
		isSpot: true,
		gpuCount,
		bidPerGpu
	})
}

// Stop pod
export const stopRunpodPod = (podId: string) => {
	return runpodRequest('PATCH', `/pods/${podId}`, {
		action: 'stop'
	})
}

// Terminate pod
export const terminateRunpodPod = (podId: string) => {
	return runpodRequest('DELETE', `/pods/${podId}`)
}

// Restart pod (quick restart without clearing state)
export const restartRunpodPod = (podId: string, gpuCount?: number, isSpot?: boolean, bidPerGpu?: number) => {
	const data: any = {
		action: 'restart',
		gpuCount: gpuCount || 1
	}
	if (isSpot) {
		data.isSpot = true
		data.bidPerGpu = bidPerGpu || 0.2
	}
	return runpodRequest('PATCH', `/pods/${podId}`, data)
}

// Reset pod (full reset - stop + resume, clears container state)
export const resetRunpodPod = (podId: string, gpuCount?: number, isSpot?: boolean, bidPerGpu?: number) => {
	const data: any = {
		action: 'reset',
		gpuCount: gpuCount || 1
	}
	if (isSpot) {
		data.isSpot = true
		data.bidPerGpu = bidPerGpu || 0.2
	}
	return runpodRequest('PATCH', `/pods/${podId}`, data)
}

// Get detailed telemetry for a running pod (CPU, memory, GPU, disk)
export const getRunpodPodTelemetry = (podId: string) => {
	return cloudRequest<any, any>({
		url: `/compute/pods/${podId}/telemetry`,
		method: 'GET',
		timeout: 30000, // 30 seconds timeout for telemetry (SSH commands can be slow)
		interceptors: commonInterceptors
	})
}

export const getRunpodApiKeyInfo = () => {
	return cloudRequest({
		url: '/compute/info',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Get GPU configurations (with extended timeout for initial load)
export const getGpuConfigs = (cloudType: 'SECURE' = 'SECURE') => {
	return cloudRequest<any, any>({
		url: `/gpu/configs?cloudType=${cloudType}`,
		method: 'GET',
		timeout: 60000, // 60 seconds timeout for GPU configs (first load can be slow)
		interceptors: commonInterceptors
	})
}

// Check GPU availability with optional GPU count
export const checkGpuAvailability = (gpuTypeId: string, gpuCount: number = 1) => {
	return runpodRequest('GET', `/gpus/availability?id=${encodeURIComponent(gpuTypeId)}&gpuCount=${gpuCount}`)
}

// GPU Pricing Admin APIs
export const getGpuPricing = () => {
	return cloudRequest<any, any>({
		url: '/gpu/pricing',
		method: 'GET',
		timeout: 60000,
		interceptors: commonInterceptors
	})
}

export const getPublicGpuPricing = () => {
	return cloudRequest<any, any>({
		url: '/gpu/pricing-public',
		method: 'GET',
		timeout: 60000,
		interceptors: commonInterceptors
	})
}

export const getRunpodPrices = () => {
	return cloudRequest<any, any>({
		url: '/gpu/runpod-prices',
		method: 'GET',
		timeout: 60000,
		interceptors: commonInterceptors
	})
}

export interface GpuPriceData {
	id: string
	displayName: string
	securePrice: number | null
	communityPrice: number | null
	memoryInGb?: number | null
	enable?: boolean
}

export const updateGpuPricing = (data: {
	globalMarkupPercent: number
	gpus?: GpuPriceData[]
	upstreamPrices?: {
		fetchedAt: string
		gpus: Array<{
			id: string
			displayName: string
			securePrice: number | null
			communityPrice: number | null
			memoryInGb: number
		}>
	}
}) => {
	return cloudRequest<any, any>({
		url: '/gpu/pricing',
		method: 'PUT',
		data,
		interceptors: commonInterceptors
	})
}

// =====================================================
// GPU Competitor Pricing APIs
// =====================================================

export interface CompetitorGpu {
	gpuModel: string
	instanceType?: string
	hourlyPrice: number
	gpuCount: number
	pricePerGpu: number
	region?: string
	memoryGb?: number | null
}

export interface CompetitorProvider {
	name: string
	enabled: boolean
	fetchable: boolean
	website?: string
	apiUrl?: string
	gpus: CompetitorGpu[]
}

export interface EtherworksGpu {
	gpuModel: string
	id: string
	pricePerGpu: number
}

export interface CompetitorPricingResponse {
	etherworks: EtherworksGpu[]
	competitors: Record<string, CompetitorProvider>
	metadata: {
		lastUpdated: string
		currency: string
		priceUnit?: string
		notes?: string
	}
	fetchedAt: string
}

export const getCompetitorPrices = (fetchLive = false) => {
	return cloudRequest<any, any>({
		url: `/gpu/competitor-prices${fetchLive ? '?fetchLive=true' : ''}`,
		method: 'GET',
		timeout: 60000, // 60 seconds for live fetch
		interceptors: commonInterceptors
	})
}

// =====================================================
// CPU Configuration APIs
// =====================================================

export interface CpuFlavorConfig {
	id: string
	displayName: string
	groupId: string
	groupName: string
	description: string
	minVcpu: number
	maxVcpu: number
	ramMultiplier: number
	diskLimitPerVcpu: number
	pricePerVcpu: number | null
	enable: boolean
}

// Get CPU flavor configurations (with extended timeout for initial load)
export const getCpuConfigs = () => {
	return cloudRequest<any, { data: { cpuFlavors: CpuFlavorConfig[] } }>({
		url: '/cpu/configs',
		method: 'GET',
		timeout: 60000, // 60 seconds timeout for CPU configs
		interceptors: commonInterceptors
	})
}

// CPU Pricing Admin APIs
export const getCpuPricing = () => {
	return cloudRequest<any, any>({
		url: '/cpu/pricing',
		method: 'GET',
		timeout: 60000,
		interceptors: commonInterceptors
	})
}

export const getRunpodCpuFlavors = () => {
	return cloudRequest<any, any>({
		url: '/cpu/runpod-flavors',
		method: 'GET',
		timeout: 60000,
		interceptors: commonInterceptors
	})
}

export interface CpuPriceData {
	id: string
	displayName: string
	pricePerVcpu: number | null
	ramMultiplier: number
	enable: boolean
}

export const updateCpuPricing = (data: {
	globalMarkupPercent: number
	cpuFlavors?: CpuPriceData[]
}) => {
	return cloudRequest<any, any>({
		url: '/cpu/pricing',
		method: 'PUT',
		data,
		interceptors: commonInterceptors
	})
}

// Create CPU pod
export const createCpuPod = (data: {
	name: string
	cpuFlavorId: string
	vcpuCount: number
	cloudType: 'SECURE'
	containerDiskInGb: number
	volumeInGb: number
	volumeMountPath?: string
	imageName: string
	startJupyter?: boolean
	env?: Array<{ key: string; value: string }>
	ports?: string
}) => {
	return runpodRequest('POST', '/pods', {
		...data,
		computeType: 'CPU'
	})
}

// Get user's pod billing history
export const getPodBillingHistory = (limit: number = 50, skip: number = 0) => {
	return runpodRequest('GET', `/billing?limit=${limit}&skip=${skip}`)
}

// =====================================================
// Data Center API
// =====================================================

// List all available data centers
export const getDatacenters = () => {
	return runpodRequest('GET', '/datacenters')
}

// =====================================================
// Network Volume API
// =====================================================

// List user's network volumes
export const getNetworkVolumes = () => {
	return runpodRequest('GET', '/network-volumes')
}

// Create a network volume
export const createNetworkVolume = (data: { name: string; size: number; dataCenterId: string }) => {
	return runpodRequest('POST', '/network-volumes', data)
}

// Update a network volume (rename / resize)
export const updateNetworkVolume = (volumeId: string, data: { name?: string; size?: number }) => {
	return runpodRequest('PATCH', `/network-volumes/${volumeId}`, data)
}

// Delete a network volume
export const deleteNetworkVolume = (volumeId: string) => {
	return runpodRequest('DELETE', `/network-volumes/${volumeId}`)
}

// Get transaction history (recharge records)
export const getTransactionHistory = (limit: number = 50, skip: number = 0) => {
	interface Transaction {
		id: string
		type: number
		type_label: string
		description: string
		amount: number
		status: string
		created_at: number
		paid_at: number
	}
	interface Res {
		code: number
		data: Transaction[]
		total: number
	}
	return cloudRequest<{}, Res>({
		url: `/order/transactions?limit=${limit}&skip=${skip}`,
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// SSH Key Management
export const getSshKeys = () => {
	interface Res {
		code: number
		data: Array<{
			id: string
			name: string
			key: string
			create_at: number
		}>
	}
	return cloudRequest<{}, Res>({
		url: '/user/ssh_keys',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

export const addSshKey = (data: { name: string; key: string }) => {
	return cloudRequest<typeof data, void>({
		url: '/user/ssh_keys',
		method: 'POST',
		data,
		interceptors: commonInterceptors
	})
}

export const deleteSshKey = (id: string) => {
	return cloudRequest<{}, void>({
		url: `/user/ssh_keys/${id}`,
		method: 'DELETE',
		interceptors: commonInterceptors
	})
}

// =====================================================
// Login Records API
// =====================================================

export interface LoginRecord {
	ts: number
	ip: string
	user_agent: string
	fingerprint: string
}

// Get recent login records
export const getLoginRecords = () => {
	return cloudRequest<null, any>({
		url: '/user/login-records',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// =====================================================
// Two-Factor Authentication (2FA) API
// =====================================================

// Get 2FA status
export const get2FAStatus = () => {
	interface Data {
		enabled: boolean
		hasBackupCodes: boolean
	}
	return cloudRequest<{}, Data>({
		url: '/user/2fa/status',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Setup 2FA (generate secret and QR code)
export const setup2FA = () => {
	interface Data {
		secret: string
		qrCode: string
		otpauth: string
	}
	return cloudRequest<{}, Data>({
		url: '/user/2fa/setup',
		method: 'POST',
		interceptors: commonInterceptors
	})
}

// Enable 2FA with verification code
export const enable2FA = (code: string) => {
	interface Data {
		backupCodes: string[]
	}
	return cloudRequest<{ code: string }, Data>({
		url: '/user/2fa/enable',
		method: 'POST',
		data: { code },
		interceptors: commonInterceptors
	})
}

// Disable 2FA
export const disable2FA = (password: string, code: string) => {
	return cloudRequest<{ password: string; code: string }, any>({
		url: '/user/2fa/disable',
		method: 'POST',
		data: { password, code },
		interceptors: commonInterceptors
	})
}

// Audit Logs API
export interface AuditLogEntry {
	_id: string
	user_id: string
	user_email: string
	action: string
	resource_type: string
	resource_id?: string
	resource_name?: string
	details?: any
	ip_address?: string
	user_agent?: string
	created_at: string
}

export interface AuditLogsData {
	logs: AuditLogEntry[]
	total: number
	limit: number
	skip: number
}

export const getAuditLogs = (params: {
	limit?: number
	skip?: number
	action?: string
	start_date?: string
	end_date?: string
	search?: string
}) => {
	const queryParams = new URLSearchParams()
	if (params.limit) queryParams.append('limit', params.limit.toString())
	if (params.skip) queryParams.append('skip', params.skip.toString())
	if (params.action) queryParams.append('action', params.action)
	if (params.start_date) queryParams.append('start_date', params.start_date)
	if (params.end_date) queryParams.append('end_date', params.end_date)
	if (params.search) queryParams.append('search', params.search)

	return cloudRequest<any, AuditLogsData>({
		url: `/audit/logs?${queryParams.toString()}`,
		method: 'GET',
		interceptors: commonInterceptors
	})
}

export type AuditAction = { value: string; label: string }

export const getAuditActions = () => {
	return cloudRequest<any, AuditAction[]>({
		url: '/audit/actions',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// ============================================
// Admin User Management APIs
// ============================================

export interface AdminUser {
	_id: string
	email: string
	role: 'user' | 'admin'
	emailVerification?: { verified?: boolean }
	balance: number
	total_recharge: number
	twoFactor?: { enabled?: boolean }
	create_at: string
	last_login_at: string | null
}

export interface AdminUserStats {
	totalUsers: number
	adminUsers: number
	totalBalance: number
	totalRecharge: number
	activeUsers: number
	twoFAUsers: number
}

export interface AdminUserListResponse {
	users: AdminUser[]
	pagination: {
		page: number
		pageSize: number
		total: number
		totalPages: number
	}
}

export interface ImportedAdminUser {
	email: string
	passwordHash: string
	role?: 'user' | 'admin'
	emailVerified?: boolean
	balance?: number
	totalRecharge?: number
	createdAt?: string
	name?: string
	preferences?: {
		language?: string
	}
	subscription?: {
		tier?: 'free' | 'plus' | 'max' | 'ultra'
		status?: 'active' | 'inactive' | 'cancelled'
		plan?: 'weekly' | 'monthly' | 'quarterly' | 'yearly'
		expiresAt?: string
	}
}

export interface AdminUserImportResponse {
	summary: {
		total: number
		imported: number
		skipped: number
		failed: number
	}
	imported: string[]
	skipped: Array<{ email: string; reason: string }>
	failed: Array<{ email: string; reason: string }>
}

// Get user statistics
export const getAdminUserStats = () => {
	return cloudRequest<any, AdminUserStats>({
		url: '/admin/users/stats',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// List all users with pagination and search
export const getAdminUsers = (params: {
	page?: number
	pageSize?: number
	search?: string
	sortField?: string
	sortOrder?: 'asc' | 'desc'
}) => {
	return cloudRequest<any, AdminUserListResponse>({
		url: '/admin/users',
		method: 'GET',
		data: params,
		interceptors: commonInterceptors
	})
}

// Modify user balance
export const updateUserBalance = (email: string, data: { amount: number; reason: string }) => {
	return cloudRequest<any, unknown>({
		url: `/admin/users/${encodeURIComponent(email)}/balance`,
		method: 'PUT',
		data,
		interceptors: commonInterceptors
	})
}

// Create a random test user
export const createRandomUser = (data: { initial_balance?: number }) => {
	return cloudRequest<any, { email: string; password: string; balance: number; message: string }>({
		url: '/admin/users/create-random',
		method: 'POST',
		data,
		interceptors: commonInterceptors
	})
}

export const importAdminUsers = (data: { users: ImportedAdminUser[] }) => {
	return cloudRequest<{ users: ImportedAdminUser[] }, AdminUserImportResponse>({
		url: '/admin/users/import',
		method: 'POST',
		data,
		interceptors: commonInterceptors
	})
}

// Change user role
export const updateUserRole = (email: string, role: 'user' | 'admin') => {
	return cloudRequest<any, unknown>({
		url: `/admin/users/${encodeURIComponent(email)}/role`,
		method: 'PUT',
		data: { role },
		interceptors: commonInterceptors
	})
}

// Force disable 2FA for a user
export const disableUser2FA = (email: string) => {
	return cloudRequest<any, unknown>({
		url: `/admin/users/${encodeURIComponent(email)}/disable-2fa`,
		method: 'POST',
		interceptors: commonInterceptors
	})
}

// Reset user password
export const resetUserPassword = (email: string) => {
	return cloudRequest<any, { email: string; password: string; message: string }>({
		url: `/admin/users/${encodeURIComponent(email)}/reset-password`,
		method: 'POST',
		interceptors: commonInterceptors
	})
}

// Delete user
export const deleteAdminUser = (email: string) => {
	return cloudRequest<any, unknown>({
		url: `/admin/users/${encodeURIComponent(email)}`,
		method: 'DELETE',
		interceptors: commonInterceptors
	})
}

// ============================================
// Admin Pod Management APIs
// ============================================

// Admin pod data - merges live RunPod state with local billing/ownership data
export interface AdminPod {
	_id: string
	pod_id: string
	user_id: string
	// Live data from RunPod API
	name?: string
	image_name?: string
	gpu_count?: number
	gpu_type?: string
	machine_id?: string
	container_disk_in_gb?: number
	volume_in_gb?: number
	volume_mount_path?: string
	desired_status?: string
	env?: string[]
	// Local data
	os_type?: string
	os_label?: string
	cloud_type?: 'SECURE'
	cost_per_hr?: number
	original_cost_per_hr?: number
	remark?: string
	auto_stop_after_seconds?: number
	is_terminated: boolean
	terminated_at?: number
	created_at?: number
	// Billing data
	billing?: {
		total_cost: number
		total_compute_cost: number
		total_container_disk_cost: number
		total_volume_disk_cost: number
		total_running_seconds: number
		total_stopped_seconds: number
		current_status?: string
	}
}

export interface AdminPodStats {
	totalPods: number
	terminatedPods: number
	runningPods: number
	stoppedPods: number
	totalHourlyCost: number
	totalBilled: number
	gpuBreakdown: Array<{ gpuType: string; count: number }>
	uniqueUsersWithPods: number
}

export interface AdminPodListResponse {
	pods: AdminPod[]
	pagination: {
		page: number
		pageSize: number
		total: number
		totalPages: number
	}
	filters: {
		gpuTypes: string[]
	}
}

// Get pod statistics
export const getAdminPodStats = () => {
	return cloudRequest<any, AdminPodStats>({
		url: '/admin/pods/stats',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// List all pods with pagination, search, and filters
export const getAdminPods = (params: {
	page?: number
	pageSize?: number
	search?: string
	status?: 'running' | 'stopped' | 'terminated'
	gpuType?: string
	cloudType?: 'SECURE'
	includeTerminated?: boolean
	sortField?: string
	sortOrder?: 'asc' | 'desc'
}) => {
	return cloudRequest<any, AdminPodListResponse>({
		url: '/admin/pods',
		method: 'GET',
		data: params,
		interceptors: commonInterceptors
	})
}

// Get single pod details
export const getAdminPod = (podId: string) => {
	return cloudRequest<any, { pod: AdminPod; billing: any; user: any; liveStatus: any }>({
		url: `/admin/pods/${encodeURIComponent(podId)}`,
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Admin pod action (start, stop, terminate)
export const adminPodAction = (podId: string, action: 'start' | 'stop' | 'terminate') => {
	return cloudRequest<any, unknown>({
		url: `/admin/pods/${encodeURIComponent(podId)}/action`,
		method: 'POST',
		data: { action },
		interceptors: commonInterceptors
	})
}

// ============================================
// Admin Payment Methods APIs
// ============================================

export interface AdminPaymentMethod {
	id: string
	user_id: string
	type: string
	is_default: boolean
	card: {
		holder_name: string
		last4: string
		brand: string
		expiry_month: string
		expiry_year: string
	}
	billing_details?: {
		address_line1?: string
		address_line2?: string
		city?: string
		state?: string
		postal_code?: string
		country?: string
	}
	created_at: number
	transaction_count: number
	total_amount: number
}

export interface AdminPaymentMethodsResponse {
	methods: AdminPaymentMethod[]
	pagination: {
		page: number
		pageSize: number
		total: number
		totalPages: number
	}
}

// Get all payment methods (admin)
export const getAdminPaymentMethods = (params: {
	page?: number
	pageSize?: number
	search?: string
}) => {
	return cloudRequest<any, AdminPaymentMethodsResponse>({
		url: '/admin/payment-methods',
		method: 'GET',
		data: params,
		interceptors: commonInterceptors
	})
}

// Delete payment method (admin)
export const deleteAdminPaymentMethod = (id: string) => {
	return cloudRequest<any, unknown>({
		url: `/admin/payment-methods/${encodeURIComponent(id)}`,
		method: 'DELETE',
		interceptors: commonInterceptors
	})
}

// Admin card transaction interface
export interface AdminCardTransaction {
	id: string
	amount: number
	status: string
	created_at: number
	paid_at: number | null
	card_brand: string | null
	card_last4: string | null
}

// Get credit card transactions for a specific user/card (admin)
export const getAdminCardTransactions = (params: {
	userId: string
	cardLast4?: string
	cardBrand?: string
}) => {
	return cloudRequest<any, AdminCardTransaction[]>({
		url: '/admin/card-transactions',
		method: 'GET',
		data: params,
		interceptors: commonInterceptors
	})
}

// Admin credit card stats interface
export interface AdminCreditCardStats {
	totalCards: number
	totalTransactions: number
	totalAmount: number
	completedTransactions: number
	completedAmount: number
	pendingTransactions: number
}

// Get overall credit card statistics (admin)
export const getAdminCreditCardStats = () => {
	return cloudRequest<any, AdminCreditCardStats>({
		url: '/admin/credit-card-stats',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// ============================================
// Admin PayPal APIs
// ============================================

export interface AdminPayPalStats {
	totalTransactions: number
	totalAmount: number
	completedTransactions: number
	completedAmount: number
	pendingTransactions: number
}

export interface AdminPayPalTransaction {
	id: string
	user_id: string
	amount: number
	status: string
	paypal_order_id: string | null
	created_at: number
	paid_at: number | null
}

export interface AdminPayPalTransactionsResponse {
	transactions: AdminPayPalTransaction[]
	pagination: {
		page: number
		pageSize: number
		total: number
		totalPages: number
	}
}

// Get PayPal statistics (admin)
export const getAdminPayPalStats = () => {
	return cloudRequest<any, AdminPayPalStats>({
		url: '/admin/paypal-stats',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Get PayPal transactions (admin)
export const getAdminPayPalTransactions = (params: {
	page?: number
	pageSize?: number
	search?: string
}) => {
	return cloudRequest<any, AdminPayPalTransactionsResponse>({
		url: '/admin/paypal-transactions',
		method: 'GET',
		data: params,
		interceptors: commonInterceptors
	})
}

// ============================================
// Support Ticket APIs
// ============================================

export interface TicketMessage {
	id: string
	sender_id: string
	sender_role: 'user' | 'admin'
	content: string
	images?: string[]
	created_at: number
}

export interface Ticket {
	id: string
	user_id: string
	title: string
	description: string
	images?: string[]
	category: 'billing' | 'technical' | 'account' | 'other'
	priority: 'low' | 'medium' | 'high' | 'urgent'
	status: 'open' | 'in_progress' | 'resolved' | 'closed'
	messages: TicketMessage[]
	message_count?: number
	created_at: number
	updated_at: number
	resolved_at: number | null
}

export interface TicketListResponse {
	tickets: Ticket[]
	total: number
}

export interface CreateTicketData {
	title: string
	description: string
	images?: string[]
	category?: 'billing' | 'technical' | 'account' | 'other'
	priority?: 'low' | 'medium' | 'high' | 'urgent'
}

// Create a new support ticket
export const createTicket = (data: CreateTicketData) => {
	return cloudRequest<CreateTicketData, { id: string; title: string; status: string; created_at: number }>({
		url: '/tickets',
		method: 'POST',
		data,
		interceptors: commonInterceptors
	})
}

// List user's tickets
export const getTickets = (params?: { status?: string; category?: string; priority?: string; limit?: number; offset?: number }) => {
	return cloudRequest<any, TicketListResponse>({
		url: '/tickets',
		method: 'GET',
		data: params,
		interceptors: commonInterceptors
	})
}

// Get ticket details
export const getTicketById = (id: string) => {
	return cloudRequest<any, Ticket>({
		url: `/tickets/${id}`,
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Add message to ticket
export const addTicketMessage = (id: string, content: string, images?: string[]) => {
	return cloudRequest<{ content: string; images?: string[] }, TicketMessage>({
		url: `/tickets/${id}/messages`,
		method: 'POST',
		data: { content, images },
		interceptors: commonInterceptors
	})
}

// Close ticket
export const closeTicket = (id: string) => {
	return cloudRequest<any, { msg: string }>({
		url: `/tickets/${id}/close`,
		method: 'PUT',
		interceptors: commonInterceptors
	})
}

// Upload ticket image
export const uploadTicketImage = (file: File) => {
	const formData = new FormData()
	formData.append('image', file)

	return fetch(`${apiBaseUrl}/tickets/images`, {
		method: 'POST',
		credentials: 'include',
		body: formData
	}).then(res => res.json())
}

// ============================================
// Admin Ticket APIs
// ============================================

export interface AdminTicketStats {
	totalTickets: number
	openTickets: number
	inProgressTickets: number
	resolvedTickets: number
	closedTickets: number
	urgentTickets: number
	highTickets: number
	categoryBreakdown: Record<string, number>
}

export interface AdminTicketListResponse {
	tickets: Ticket[]
	pagination: {
		page: number
		pageSize: number
		total: number
		totalPages: number
	}
}

// Admin: Get sidebar badge counts (tickets needing reply)
export const getAdminBadgeCounts = () => {
	return cloudRequest<any, { ticketCount: number }>({
		url: '/admin/badge-counts',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Admin: Get ticket statistics
export const adminGetTicketStats = () => {
	return cloudRequest<any, AdminTicketStats>({
		url: '/admin/tickets/stats',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Admin: List all tickets with filters
export const adminGetTickets = (params?: {
	page?: number
	pageSize?: number
	search?: string
	status?: string
	category?: string
	priority?: string
	sortField?: string
	sortOrder?: 'asc' | 'desc'
}) => {
	return cloudRequest<any, AdminTicketListResponse>({
		url: '/admin/tickets',
		method: 'GET',
		data: params,
		interceptors: commonInterceptors
	})
}

// Admin: Get ticket details
export const adminGetTicketById = (id: string) => {
	return cloudRequest<any, { ticket: Ticket; user: { email: string; balance: number; role: string } | null }>({
		url: `/admin/tickets/${id}`,
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Admin: Reply to ticket
export const adminReplyTicket = (id: string, content: string, images?: string[]) => {
	return cloudRequest<{ content: string; images?: string[] }, TicketMessage>({
		url: `/admin/tickets/${id}/messages`,
		method: 'POST',
		data: { content, images },
		interceptors: commonInterceptors
	})
}

// Admin: Update ticket status
export const adminUpdateTicketStatus = (id: string, status: 'open' | 'in_progress' | 'resolved' | 'closed') => {
	return cloudRequest<{ status: string }, { id: string; status: string; updated_at: number }>({
		url: `/admin/tickets/${id}/status`,
		method: 'PUT',
		data: { status },
		interceptors: commonInterceptors
	})
}

// ============================================
// Admin Selling Copy APIs
// ============================================

export interface SellingCopyConfig {
	languages: Record<string, string>
	lastUpdated: string
}

// Admin: Get selling copy config
export const getSellingCopyConfig = () => {
	return cloudRequest<any, SellingCopyConfig>({
		url: '/admin/selling-copy',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Admin: Save selling copy config
export const saveSellingCopyConfig = (languages: Record<string, string>) => {
	return cloudRequest<{ languages: Record<string, string> }, SellingCopyConfig>({
		url: '/admin/selling-copy',
		method: 'PUT',
		data: { languages },
		interceptors: commonInterceptors
	})
}

